/**
 * Fjalori i rregullimeve të pagës — një burim i vetëm për etiketat dhe ngjyrat.
 *
 * Pasqyron ADJ_CATEGORIES / ADJ_TYPES te backend/server.js. Kur shtohet një
 * lloj, shtohet në të dy anët: serveri e refuzon një `type` jashtë listës së
 * kategorisë (e zëvendëson me të parin), ndaj një etiketë e re pa shokun e vet
 * te serveri do të ruhej si diçka tjetër nga ç'zgjodhi përdoruesi.
 */

export const CATEGORIES = ['addition', 'advance', 'reimbursement', 'deduction'];

// Çiftet bg/color janë ato që përdor tashmë aplikacioni te çipat (shih
// LeaveAdjustmentsPage: #DCFCE7/#15803D, #EDE9FE/#6D28D9, #FEE2E2/#B91C1C).
// Jeshile shtesat, kaltër avancet, vjollcë rimbursimet, portokalli ndalesat —
// drejtimi lexohet nga ngjyra para se të lexohet teksti.
export const CATEGORY_META = {
  addition: {
    label: 'Shtesat',   singular: 'Shtesë',
    color: '#15803D', bg: '#DCFCE7', border: '#BBF7D0', soft: '#F0FDF4',
    sign: '+', hint: 'I shtohet bruto-s dhe tatohet.',
  },
  advance: {
    label: 'Avancet',   singular: 'Avanc',
    color: '#1D4ED8', bg: '#DBEAFE', border: '#BFDBFE', soft: '#EFF6FF',
    sign: '−', hint: 'Para të dhëna paraprakisht — zbriten nga neto.',
  },
  reimbursement: {
    label: 'Rimbursimet', singular: 'Rimbursim',
    color: '#6D28D9', bg: '#EDE9FE', border: '#DDD6FE', soft: '#F5F3FF',
    sign: '',  hint: 'Paguhet veçmas dhe nuk tatohet — nuk hyn në përllogaritjen e pagës.',
  },
  deduction: {
    label: 'Ndalesat',  singular: 'Ndalesë',
    color: '#C2410C', bg: '#FFEDD5', border: '#FED7AA', soft: '#FFF7ED',
    sign: '−', hint: 'Zbritet nga neto.',
  },
};

export const TYPES = {
  addition: [
    { value: 'bonus',       label: 'Bonus' },
    { value: 'commission',  label: 'Komision' },
    { value: 'performance', label: 'Shtesë për performancë' },
    { value: 'other',       label: 'Shtesë tjetër' },
  ],
  advance: [
    { value: 'advance',     label: 'Avanc i ri' },
  ],
  reimbursement: [
    { value: 'travel',      label: 'Shpenzime udhëtimi' },
    { value: 'meal',        label: 'Ushqim' },
    { value: 'transport',   label: 'Transport' },
    { value: 'equipment',   label: 'Pajisje pune' },
    { value: 'other',       label: 'Shpenzim tjetër' },
  ],
  deduction: [
    { value: 'advance_repayment', label: 'Kthimi i avancit' },
    // Qëllimisht nuk ka "Pushimet" si ndalesë e përgjithshme: një pushim i
    // paguar nuk ndalet, dhe termi i përbashkët i fsheh të dyja rastet.
    { value: 'unpaid_leave',      label: 'Pushimi pa pagesë' },
    { value: 'unexcused_absence', label: 'Mungesa e paarsyetuar' },
    { value: 'equipment_damage',  label: 'Dëmtimi i pajisjeve' },
    { value: 'financial_penalty', label: 'Masë financiare' },
    { value: 'other',             label: 'Ndalesë tjetër' },
  ],
};

export const STATUSES = [
  { value: 'draft',     label: 'Draft',              color: 'default' },
  { value: 'pending',   label: 'Në pritje',          color: 'warning' },
  { value: 'approved',  label: 'Aprovuar',           color: 'success' },
  { value: 'rejected',  label: 'Refuzuar',           color: 'error'   },
  { value: 'processed', label: 'Procesuar në pagë',  color: 'info'    },
];

// ── Procesi ─────────────────────────────────────────────────────────────────
// Pasqyron `backend/services/payrollAdjustmentWorkflow.js`. Kalimet rrinë në të
// dy anët sepse UI-ja duhet të dijë PARA klikimit se ç'buton ka kuptim, kurse
// serveri nuk mund t'i besojë klientit se e dinte. Testi
// `frontend/src/config/payrollAdjustments.test.js` i mban të njëjta — një kalim
// i shtuar vetëm në një anë do të prodhonte butona që serveri i refuzon, ose
// veprime të lejuara që UI-ja nuk i tregon.

/** Sa shenja duhet të ketë një arsye refuzimi për t'u quajtur arsye. */
export const MIN_REASON = 5;

export const WORKFLOW = {
  submit: {
    from: ['draft', 'rejected'], to: 'pending',
    decider: false, selfBlocked: false, reason: false,
    label: 'Dërgo për aprovim', tone: 'primary',
    hint: 'Rregullimi kalon në radhën e shqyrtimit. Deri atëherë nuk prek asnjë pagë.',
  },
  approve: {
    from: ['draft', 'pending'], to: 'approved',
    decider: true, selfBlocked: true, reason: false,
    label: 'Aprovo', tone: 'success',
    hint: 'Pas aprovimit, shuma hyn në përllogaritjen e pagës së periudhës.',
  },
  reject: {
    from: ['draft', 'pending'], to: 'rejected',
    decider: true, selfBlocked: true, reason: true,
    label: 'Refuzo', tone: 'error',
    hint: 'Arsyeja shkon te punonjësi — pa të, refuzimi nuk shpjegon asgjë.',
  },
  revoke: {
    from: ['approved'], to: 'pending',
    decider: true, selfBlocked: false, reason: false,
    label: 'Rikthe në pritje', tone: 'warning',
    hint: 'Aprovimi hiqet dhe shuma nuk hyn më në pagë derisa të vendoset sërish.',
  },
};

export const ACTION_ORDER = ['approve', 'reject', 'submit', 'revoke'];

/**
 * A ka kuptim `action` mbi `row` për këtë përdorues?
 *
 * `actor` = { isDecider, workerId, userId } — `workerId` është punëtori i lidhur
 * me llogarinë, që rregulli "askush nuk vendos për vetveten" të mund të matet.
 */
export const canRun = (action, row, actor = {}) => {
  const def = WORKFLOW[action];
  if (!def || !row) return false;
  if (row.status === 'processed') return false;
  if (!def.from.includes(row.status)) return false;
  if (def.decider && !actor.isDecider) return false;
  const sameWorker = row.workerId != null && actor.workerId != null
    && String(row.workerId) === String(actor.workerId);
  if (def.selfBlocked && sameWorker) return false;
  if (action === 'submit' && !actor.isDecider) {
    const isCreator = row.createdBy != null && actor.userId != null
      && String(row.createdBy) === String(actor.userId);
    if (!isCreator && !sameWorker) return false;
  }
  return true;
};

/** Veprimet e mundshme, në radhën në të cilën shfaqen. */
export const allowedActions = (row, actor = {}) =>
  ACTION_ORDER.filter((a) => canRun(a, row, actor));

/** A pranohet kjo arsye refuzimi? Njësoj si serveri, që butoni të mos gënjejë. */
export const reasonOk = (text) => String(text || '').trim().replace(/\s+/g, ' ').length >= MIN_REASON;

// ── Identiteti i dokumentit të vendimit ─────────────────────────────────────
// Rrinë KËTU e jo te `services/adjustmentDecisionPdf.js` sepse nuk kanë të bëjnë
// me vizatimin: numri i protokollit dhe emri i skedarit janë fjalor, si etiketat
// më sipër. Ana praktike: testohen pa e ngarkuar jsPDF-në.

/** Numri i protokollit — i qëndrueshëm, sepse del nga vetë rreshti e jo nga ora. */
export const decisionRef = (row) =>
  `${String(row?.id ?? '').padStart(4, '0')}/${row?.periodYear || new Date().getFullYear()}`;

/** Emri i skedarit: pa shkronja shqipe, që të mbijetojë çdo sistem skedarësh. */
export const decisionFilename = (row) => {
  const name = String(row?.workerName || '')
    .toLowerCase().replace(/ë/g, 'e').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `vendim-${name || 'punonjesi'}-${decisionRef(row).replace('/', '-')}.pdf`;
};

/** Etiketat e kronologjisë — `action` vjen nga payroll_adjustment_events. */
export const EVENT_LABELS = {
  created:   'Krijuar',
  requested: 'Kërkuar nga punonjësi',
  submit:    'Dërguar për aprovim',
  edited:    'Ndryshuar',
  approve:   'Aprovuar',
  reject:    'Refuzuar',
  revoke:    'Rikthyer në pritje',
  process:   'Procesuar në pagë',
  unprocess: 'Hequr nga lista e pagës',
  emailed:   'Vendimi u dërgua me email',
};

export const REPAYMENT_METHODS = [
  { value: 'lump_sum',     label: 'Njëherësh' },
  { value: 'installments', label: 'Me këste' },
];

/** Kategoritë ku dokumenti mbështetës pritet normalisht. */
export const DOCUMENT_CATEGORIES = new Set(['reimbursement', 'deduction']);

export const typeLabel = (category, value) =>
  (TYPES[category] || []).find((t) => t.value === value)?.label || value || '—';

export const statusMeta = (value) =>
  STATUSES.find((s) => s.value === value) || { label: value || '—', color: 'default' };
